import { Inject, Injectable } from '@angular/core';

import { NgRedux } from '@angular-redux/store';

import {
    CartStatus,
    DataTransformService,
    LogService,
    NotificationService,
    PaymentChannelCode,
    PaymentMethod,
    PaymentType,
    ShoppingCart
} from '@ridango/core';

import { Observable, of } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, switchMap, take } from 'rxjs/operators';

import { PaymentMethodsState } from './+state';
import { PaymentMethodsActions } from './+state/payment-methods.actions';
import { delayedResponseRetry } from '@ridango/core/lib/operators/delayed-reponse-retry';
import Env from '@ridango/core/env';
import { CartPaymentMethodsDetails } from '../purchase/purchase.model';
import { CustomerAccountsFacade } from '../customer-accounts';

@Injectable()
export class PaymentMethodsService {
    constructor(
        @Inject('ENV') private env: Env,
        private ls: LogService,
        private http: HttpClient,
        private actions: PaymentMethodsActions,
        private ngRedux: NgRedux<PaymentMethodsState>,
        private notifications: NotificationService,
        private dataTransformer: DataTransformService,
        private customerAccountsFacade: CustomerAccountsFacade
    ) {}

    fetchAllMethods(cart: ShoppingCart = null, _token: string = null): Observable<PaymentMethod[]> {
        // TODO: change authorization when auth is resolved
        // const headers = new Headers();
        // headers.append( 'Authorization', 'Bearer ' + token );
        // const options = new RequestOptions( { headers: headers } );

        const channel = this.env.CHANNEL;
        let url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/payment/channel/${channel}/methods`;
        if (cart && !this.env.IS_CORPORATE && !this.env.PURCHASE?.GENERIC_PAYMENT_METHODS) {
            url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/payment/channel/${channel}/cart/${cart.shopping_cart_id}/methods`;
        }

        if (this.env.IS_NEW_AFC) {
            return of([
                {
                    payment_id: 1,
                    payment_type: PaymentType.ADYEN,
                    fee: 1,
                    fee_vat: 1,
                    name: 'mock',
                    banklink_account_id: 1,
                    code: '200',
                    translations: [
                        {
                            lang: 'en',
                            field: 'mock',
                            translation: 'mock'
                        }
                    ],
                    order: 1,
                    display_tokens_first: true
                }
            ]);
        }

        // TODO: Check if this works
        return this.http.get(url).pipe(
            map((data: PaymentMethod[]) => {
                const methods =
                    this.env.SHOW_PAYMENT_PORTAL === false
                        ? data.filter((m) => m.code !== PaymentChannelCode.PAYMENT_PORTAL)
                        : data;
                return methods.map((m) => {
                    let method = new PaymentMethod();
                    method = { ...method, ...m };
                    method.translations = this.dataTransformer.transformObjectTranslations(m, 'descr');
                    return method;
                });
            })
        );
    }

    fetchAllCorpMethods(cart: ShoppingCart = null, _token: string = null): Observable<PaymentMethod[]> {
        // TODO: change authorization when auth is resolved
        // const headers = new Headers();
        // headers.append( 'Authorization', 'Bearer ' + token );
        // const options = new RequestOptions( { headers: headers } );

        let url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/payment/channel/web_corp/methods`;
        if (cart) {
            // cart based methods
            url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/payment/channel/web_corp/cart/${cart.shopping_cart_id}/methods`;
        }
        // TODO: Check if this works
        return this.http.get(url).pipe(
            map((data: PaymentMethod[]) => {
                return data.map((m) => {
                    let method = new PaymentMethod();
                    method = { ...method, ...m };
                    method.translations = this.dataTransformer.transformObjectTranslations(m, 'descr');
                    return method;
                });
            })
        );
    }

    setAllMethods(methods: PaymentMethod[]): void {
        const allIds = methods.map((m) => m.payment_id);
        const normalized = Object.fromEntries(methods.map((m) => [m.payment_id, m]));

        this.ngRedux.dispatch(this.actions.fetchSuccess({ allIds: allIds, methods: normalized, all: methods }));

        if (methods.length === 1) {
            this.setSelectedMethod(methods[0]);
        }
    }

    setSelectedMethod(method: PaymentMethod) {
        this.ngRedux.dispatch(this.actions.setSelected(method));
    }

    setSelectedMethodPin(pin: any) {
        this.ngRedux.dispatch(this.actions.setSelectedMethodPin(pin));
    }

    fetchStarted() {
        this.ngRedux.dispatch(this.actions.fetchStart());
    }

    fetchStartedWithEpic(cart) {
        this.ngRedux.dispatch(this.actions.fetchStartWithEpic(cart));
    }

    fetchFailed(err) {
        this.ngRedux.dispatch(this.actions.fetchFailed(err));
    }

    getAccountPaymentMethods(_token: string = null): Observable<any[]> {
        // change
        const url = `${this.env.API_BASE_URL}/everypay/tokens/channel/web/region/${this.env.REGION_ID}`;
        return this.http.get(url).pipe(
            map((a: any[]) => {
                a.map((c: any) => {
                    c.display = '**** ' + c.display;
                });
                return a;
            })
        );
    }

    setSelectedAccountPaymentMethod(token) {
        this.ngRedux.dispatch(this.actions.setSelectedAccountPaymentMethod(token));
    }

    setAllAccountPaymentMethods(cards) {
        this.ngRedux.dispatch(this.actions.setAllAccountPaymentMethods(cards));
    }

    deleteAccountPaymentMethod(tokenId) {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/customer-account/payment-token/${tokenId}`;
        return this.http.delete(url);
    }

    requestAccountPaymentMethodToken(locale, route) {
        let headers = new HttpHeaders();
        if (locale !== '') {
            headers = headers.set('X-app-lang', locale);
        }

        const url = `${this.env.API_BASE_URL}/everypay/token_request/channel/web/region/${this.env.REGION_ID}`;
        const payload = {
            return_url: `${this.env.BASE_URL}${locale}/${route}`
        };

        return this.http.post(url, payload, { headers: headers });
    }

    putAccountPaymentMethodConfirm(paymentReference) {
        const url = `${this.env.API_BASE_URL}/everypay/token_request/status/channel/web/region/${this.env.REGION_ID}`;
        const payload = {
            payment_reference: paymentReference
        };

        return this.http.put(url, payload);
    }

    fetchAdyenSession(sessionRequest) {
        const url = `${this.env.CORE_API_URL}/customer/v3/region/${this.env.REGION_ID}/payment/adyen/session`;
        return this.http.post(url, sessionRequest);
    }

    fetchAdyenPaymentStatus(token: string) {
        const url = `${this.env.CORE_API_URL}/customer/v2/region/${this.env.REGION_ID}/shoppingcart/status`;

        const headers = new HttpHeaders().set('Authorization', `Cart ${token}`);
        const options = {
            headers: headers
        };

        return this.http.get(url, options).pipe(delayedResponseRetry({ status: CartStatus.VALID }, 500, 20));
    }

    getPaymentMethods(): Observable<CartPaymentMethodsDetails> {
        return this.customerAccountsFacade.selectedAccount$.pipe(
            take(1),
            switchMap((selectedAccount) => {
                const baseUrl = `${this.env.WMB.WMB_PUBLIC_API}/v1/payment/methods`;
                const url = new URL(baseUrl);

                if (selectedAccount && selectedAccount.type.code === 'CORPORATE') {
                    url.searchParams.set('customerAccountId', selectedAccount.id.toString());
                }

                return this.http.get<CartPaymentMethodsDetails>(url.toString());
            })
        );
    }

    addCard(request: {
        posPaymentMethodId: string;
        redirectUrl: string;
        languageCode?: string;
    }): Observable<{ addCardUrl: string; orderReference: string }> {
        const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/payment/methods/card/add`;
        return this.http.post<{ addCardUrl: string; orderReference: string }>(url, request);
    }

    deleteCard(cardId: string): Observable<void> {
        const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/payment/methods/card/${cardId}`;
        return this.http.delete<void>(url);
    }
}
